import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { HexColorPicker } from 'react-colorful';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Save, Eye, EyeOff, Settings, Palette, Layout, Type, Image, Video,
  GripVertical, Plus, Trash2, ChevronDown, ChevronUp, X, Loader2,
  Monitor, Smartphone, RotateCcw, Check, AlertCircle, PanelRightOpen, PanelRightClose,
  Quote, Phone, ArrowRight, CheckCircle2, Globe, Navigation, FileText, 
  Link as LinkIcon, ImageIcon, History, Copy, ExternalLink
} from 'lucide-react';
import '../styles/VisualEditor.css';


const VisualEditor = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [currentPage, setCurrentPage] = useState(searchParams.get('page') || 'home');
  const [pageContent, setPageContent] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [activeTab, setActiveTab] = useState('sections');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [pages, setPages] = useState([]);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/login');
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchContent();
      fetchPages();
    }
  }, [user, currentPage]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const [pageRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cms/pages/${currentPage}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/cms/settings`, { withCredentials: true })
      ]);
      setPageContent(pageRes.data);
      setSiteSettings(settingsRes.data);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load content:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cms/pages`, { withCredentials: true });
      setPages(res.data);
    } catch (err) {
      console.error('Failed to load pages:', err);
    }
  };

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cms/pages/${currentPage}/versions`, { withCredentials: true });
      setVersions(res.data);
      setShowVersions(true);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const restoreVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to restore this version? Current changes will be saved as a backup.')) return;
    
    try {
      setSaving(true);
      const res = await axios.post(`${API_URL}/api/cms/pages/${currentPage}/restore/${versionId}`, {}, { withCredentials: true });
      setPageContent(res.data);
      setShowVersions(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Failed to restore version:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await Promise.all([
        axios.put(`${API_URL}/api/cms/pages/${currentPage}`, pageContent, { withCredentials: true }),
        axios.put(`${API_URL}/api/cms/settings`, siteSettings, { withCredentials: true })
      ]);
      setHasChanges(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const duplicatePage = async () => {
    const newSlug = prompt('Enter slug for the new page:', `${currentPage}-copy`);
    if (!newSlug) return;
    
    const newTitle = prompt('Enter title for the new page:', `Copy of ${pageContent?.title || currentPage}`);
    if (!newTitle) return;
    
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/cms/pages/${currentPage}/duplicate`, {
        new_slug: newSlug,
        new_title: newTitle
      }, { withCredentials: true });
      await fetchPages();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Failed to duplicate page:', err);
      alert(err.response?.data?.detail || 'Failed to duplicate page');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = useCallback((sectionId, updates) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
    setHasChanges(true);
  }, []);

  const updateSectionContent = useCallback((sectionId, contentUpdates) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, content: { ...s.content, ...contentUpdates } } : s
      )
    }));
    setHasChanges(true);
  }, []);

  const updateSectionStyle = useCallback((sectionId, styleUpdates) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, style: { ...s.style, ...styleUpdates } } : s
      )
    }));
    setHasChanges(true);
  }, []);

  const updateSiteSettings = useCallback((updates) => {
    setSiteSettings(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  }, []);

  const updateBrand = useCallback((updates) => {
    setSiteSettings(prev => ({
      ...prev,
      brand: { ...prev.brand, ...updates }
    }));
    setHasChanges(true);
  }, []);

  const updateColor = useCallback((colorKey, value) => {
    setSiteSettings(prev => ({
      ...prev,
      colors: { ...prev.colors, [colorKey]: value }
    }));
    setHasChanges(true);
  }, []);

  const updateNavigation = useCallback((updates) => {
    setSiteSettings(prev => ({
      ...prev,
      navigation: { ...prev.navigation, ...updates }
    }));
    setHasChanges(true);
  }, []);

  const updateSEO = useCallback((updates) => {
    setSiteSettings(prev => ({
      ...prev,
      seo: { ...prev.seo, ...updates }
    }));
    setHasChanges(true);
  }, []);

  const updatePageSEO = useCallback((updates) => {
    setPageContent(prev => ({
      ...prev,
      seo: { ...prev.seo, ...updates }
    }));
    setHasChanges(true);
  }, []);

  const toggleSection = useCallback((sectionId) => {
    updateSection(sectionId, { 
      enabled: !pageContent.sections.find(s => s.id === sectionId).enabled 
    });
  }, [pageContent, updateSection]);

  const deleteSection = useCallback((sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
    setHasChanges(true);
    if (activeSection === sectionId) setActiveSection(null);
  }, [activeSection]);

  const duplicateSection = useCallback((sectionId) => {
    const section = pageContent.sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const newSection = {
      ...JSON.parse(JSON.stringify(section)),
      id: `${section.type}-${Date.now()}`
    };
    
    const index = pageContent.sections.findIndex(s => s.id === sectionId);
    const newSections = [...pageContent.sections];
    newSections.splice(index + 1, 0, newSection);
    
    setPageContent(prev => ({
      ...prev,
      sections: newSections
    }));
    setHasChanges(true);
    setActiveSection(newSection.id);
  }, [pageContent]);

  const addSection = useCallback((type) => {
    const newSection = createNewSection(type);
    setPageContent(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setHasChanges(true);
    setActiveSection(newSection.id);
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setPageContent(prev => {
        const oldIndex = prev.sections.findIndex(s => s.id === active.id);
        const newIndex = prev.sections.findIndex(s => s.id === over.id);
        return {
          ...prev,
          sections: arrayMove(prev.sections, oldIndex, newIndex)
        };
      });
      setHasChanges(true);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="editor-loading">
        <Loader2 size={48} className="spinner" />
        <p>Loading editor...</p>
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <div className="visual-editor">
      {/* Editor Header */}
      <header className="editor-header">
        <div className="editor-header-left">
          <select 
            value={currentPage} 
            onChange={(e) => setCurrentPage(e.target.value)}
            className="page-selector"
          >
            {pages.map(p => (
              <option key={p.slug} value={p.slug}>{p.title || p.slug}</option>
            ))}
          </select>
          
          <div className="preview-toggle">
            <button 
              className={previewMode === 'desktop' ? 'active' : ''}
              onClick={() => setPreviewMode('desktop')}
              title="Desktop Preview"
            >
              <Monitor size={18} />
            </button>
            <button 
              className={previewMode === 'mobile' ? 'active' : ''}
              onClick={() => setPreviewMode('mobile')}
              title="Mobile Preview"
            >
              <Smartphone size={18} />
            </button>
          </div>
        </div>
        
        <div className="editor-header-center">
          <button
            className={`btn-preview-toggle ${showPreview ? 'active' : ''}`}
            onClick={() => setShowPreview(!showPreview)}
            title={showPreview ? 'Hide Preview' : 'Show Preview'}
          >
            {showPreview ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          
          <button
            className="btn-icon"
            onClick={fetchVersions}
            title="Version History"
          >
            <History size={18} />
          </button>
          
          <button
            className="btn-icon"
            onClick={duplicatePage}
            title="Duplicate Page"
          >
            <Copy size={18} />
          </button>
          
          <a 
            href={`/${currentPage === 'home' ? '' : currentPage}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="preview-link"
          >
            <ExternalLink size={16} />
            View Live
          </a>
        </div>
        
        <div className="editor-header-right">
          {saveStatus === 'success' && (
            <span className="save-status success">
              <Check size={16} /> Saved!
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="save-status error">
              <AlertCircle size={16} /> Error saving
            </span>
          )}
          
          <button 
            className="btn-reset"
            onClick={fetchContent}
            disabled={!hasChanges}
            title="Discard Changes"
          >
            <RotateCcw size={18} />
          </button>
          
          <button 
            className={`btn-save ${hasChanges ? 'has-changes' : ''}`}
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <Loader2 size={18} className="spinner" />
            ) : (
              <Save size={18} />
            )}
            {hasChanges ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </header>

      {/* Version History Modal */}
      {showVersions && (
        <div className="modal-overlay" onClick={() => setShowVersions(false)}>
          <div className="modal-content versions-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><History size={20} /> Version History</h3>
              <button onClick={() => setShowVersions(false)}><X size={20} /></button>
            </div>
            <div className="versions-list">
              {versions.length === 0 ? (
                <p className="no-versions">No previous versions found.</p>
              ) : (
                versions.map(v => (
                  <div key={v.id} className="version-item">
                    <div className="version-info">
                      <span className="version-date">
                        {new Date(v.created_at).toLocaleString()}
                      </span>
                      <span className="version-author">by {v.created_by}</span>
                      {v.note && <span className="version-note">{v.note}</span>}
                    </div>
                    <button 
                      className="btn-restore"
                      onClick={() => restoreVersion(v.id)}
                    >
                      Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`editor-body ${showPreview ? 'with-preview' : ''}`}>
        {/* Left Sidebar - Section List & Settings */}
        <aside className="editor-sidebar">
          <div className="sidebar-tabs">
            <button 
              className={activeTab === 'sections' ? 'active' : ''}
              onClick={() => setActiveTab('sections')}
              title="Page Sections"
            >
              <Layout size={18} />
              Sections
            </button>
            <button 
              className={activeTab === 'theme' ? 'active' : ''}
              onClick={() => setActiveTab('theme')}
              title="Theme & Colors"
            >
              <Palette size={18} />
              Theme
            </button>
            <button 
              className={activeTab === 'brand' ? 'active' : ''}
              onClick={() => setActiveTab('brand')}
              title="Brand & Identity"
            >
              <Globe size={18} />
              Brand
            </button>
            <button 
              className={activeTab === 'nav' ? 'active' : ''}
              onClick={() => setActiveTab('nav')}
              title="Navigation"
            >
              <Navigation size={18} />
              Nav
            </button>
            <button 
              className={activeTab === 'seo' ? 'active' : ''}
              onClick={() => setActiveTab('seo')}
              title="SEO Settings"
            >
              <FileText size={18} />
              SEO
            </button>
          </div>

          <div className="sidebar-content">
            {activeTab === 'sections' && pageContent && (
              <SectionsPanel
                sections={pageContent.sections}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                toggleSection={toggleSection}
                deleteSection={deleteSection}
                duplicateSection={duplicateSection}
                addSection={addSection}
                onDragEnd={handleDragEnd}
                sensors={sensors}
              />
            )}

            {activeTab === 'theme' && siteSettings && (
              <ThemePanel
                settings={siteSettings}
                updateColor={updateColor}
                showColorPicker={showColorPicker}
                setShowColorPicker={setShowColorPicker}
              />
            )}

            {activeTab === 'brand' && siteSettings && (
              <BrandPanel
                settings={siteSettings}
                updateBrand={updateBrand}
              />
            )}

            {activeTab === 'nav' && siteSettings && (
              <NavigationPanel
                settings={siteSettings}
                updateNavigation={updateNavigation}
              />
            )}

            {activeTab === 'seo' && siteSettings && pageContent && (
              <SEOPanel
                globalSEO={siteSettings.seo}
                pageSEO={pageContent.seo}
                updateGlobalSEO={updateSEO}
                updatePageSEO={updatePageSEO}
                currentPage={currentPage}
              />
            )}
          </div>
        </aside>

        {/* Main Content - Section Editor */}
        <main className="editor-main">
          {activeSection && pageContent ? (
            <SectionEditor
              section={pageContent.sections.find(s => s.id === activeSection)}
              updateContent={updateSectionContent}
              updateStyle={updateSectionStyle}
              siteSettings={siteSettings}
            />
          ) : (
            <div className="no-section-selected">
              <Layout size={48} />
              <h3>Select a section to edit</h3>
              <p>Click on any section in the left sidebar to start editing</p>
            </div>
          )}
        </main>

        {/* Live Preview Panel */}
        {showPreview && pageContent && siteSettings && (
          <aside className={`preview-panel ${previewMode}`}>
            <div className="preview-header">
              <span>Live Preview</span>
              <span className="preview-mode-label">{previewMode === 'desktop' ? 'Desktop' : 'Mobile'}</span>
            </div>
            <div className="preview-container">
              <div className="preview-frame">
                <LivePreview 
                  sections={pageContent.sections}
                  settings={siteSettings}
                  activeSection={activeSection}
                  previewMode={previewMode}
                />
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SECTIONS PANEL
// ============================================================================

const SectionsPanel = ({ sections, activeSection, setActiveSection, toggleSection, deleteSection, duplicateSection, addSection, onDragEnd, sensors }) => {
  const [showAddMenu, setShowAddMenu] = useState(false);

  const sectionTypes = [
    { type: 'hero', label: 'Hero Section', icon: Image },
    { type: 'intro', label: 'Introduction', icon: Type },
    { type: 'text', label: 'Text Block', icon: Type },
    { type: 'bulletList', label: 'Bullet List', icon: CheckCircle2 },
    { type: 'features', label: 'Features Grid', icon: Layout },
    { type: 'testimonials', label: 'Testimonials', icon: Quote },
    { type: 'cta', label: 'Call to Action', icon: ArrowRight },
    { type: 'contact', label: 'Contact Form', icon: Phone },
    { type: 'faq', label: 'FAQ Section', icon: FileText },
    { type: 'courseShowcase', label: 'Course Showcase', icon: Layout },
    { type: 'video', label: 'Video Section', icon: Video },
    { type: 'imageGallery', label: 'Image Gallery', icon: ImageIcon },
    { type: 'logoStrip', label: 'Logo Strip', icon: ImageIcon },
  ];

  return (
    <div className="sections-panel">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          {sections.map((section) => (
            <SortableSection
              key={section.id}
              section={section}
              isActive={activeSection === section.id}
              onClick={() => setActiveSection(section.id)}
              onToggle={() => toggleSection(section.id)}
              onDelete={() => deleteSection(section.id)}
              onDuplicate={() => duplicateSection(section.id)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <div className="add-section-wrapper">
        <button 
          className="btn-add-section"
          onClick={() => setShowAddMenu(!showAddMenu)}
        >
          <Plus size={18} />
          Add Section
        </button>
        
        {showAddMenu && (
          <div className="add-section-menu">
            {sectionTypes.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                onClick={() => {
                  addSection(type);
                  setShowAddMenu(false);
                }}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Sortable Section Item
const SortableSection = ({ section, isActive, onClick, onToggle, onDelete, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`section-item ${isActive ? 'active' : ''} ${!section.enabled ? 'disabled' : ''}`}
    >
      <div className="section-drag" {...attributes} {...listeners}>
        <GripVertical size={16} />
      </div>
      <div className="section-info" onClick={onClick}>
        <span className="section-type">{getSectionTypeLabel(section.type)}</span>
        <span className="section-id">{section.id}</span>
      </div>
      <div className="section-actions">
        <button onClick={onDuplicate} title="Duplicate">
          <Copy size={14} />
        </button>
        <button onClick={onToggle} title={section.enabled ? 'Hide' : 'Show'}>
          {section.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <button onClick={onDelete} className="delete" title="Delete">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// THEME PANEL
// ============================================================================

const ThemePanel = ({ settings, updateColor, showColorPicker, setShowColorPicker }) => {
  const colorOptions = [
    { key: 'primary', label: 'Primary Color' },
    { key: 'secondary', label: 'Secondary Color' },
    { key: 'accent', label: 'Accent Color' },
    { key: 'background', label: 'Background' },
    { key: 'backgroundLight', label: 'Light Background' },
    { key: 'text', label: 'Text Color' },
    { key: 'textMuted', label: 'Muted Text' },
    { key: 'success', label: 'Success' },
    { key: 'warning', label: 'Warning' },
    { key: 'error', label: 'Error' },
  ];

  return (
    <div className="theme-panel">
      <h3>Brand Colors</h3>
      
      {colorOptions.map(({ key, label }) => (
        <div key={key} className="color-option">
          <label>{label}</label>
          <div className="color-input-wrapper">
            <button
              className="color-preview"
              style={{ backgroundColor: settings.colors?.[key] || '#000' }}
              onClick={() => setShowColorPicker(showColorPicker === key ? null : key)}
            />
            <input
              type="text"
              value={settings.colors?.[key] || ''}
              onChange={(e) => updateColor(key, e.target.value)}
            />
          </div>
          {showColorPicker === key && (
            <div className="color-picker-popover">
              <div className="color-picker-cover" onClick={() => setShowColorPicker(null)} />
              <HexColorPicker
                color={settings.colors?.[key] || '#000'}
                onChange={(color) => updateColor(key, color)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// BRAND PANEL
// ============================================================================

const BrandPanel = ({ settings, updateBrand }) => {
  const brand = settings.brand || {};
  const logo = brand.logo || {};

  return (
    <div className="brand-panel">
      <h3>Brand & Identity</h3>
      
      <div className="form-group">
        <label>Site Name</label>
        <input
          type="text"
          value={brand.siteName || ''}
          onChange={(e) => updateBrand({ siteName: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Tagline</label>
        <input
          type="text"
          value={brand.tagline || ''}
          onChange={(e) => updateBrand({ tagline: e.target.value })}
        />
      </div>
      
      <h4>Logos</h4>
      
      <div className="form-group">
        <label>Primary Logo URL</label>
        <input
          type="text"
          value={logo.primary || ''}
          onChange={(e) => updateBrand({ logo: { ...logo, primary: e.target.value } })}
          placeholder="https://..."
        />
        {logo.primary && (
          <img src={logo.primary} alt="Logo Preview" className="logo-preview" />
        )}
      </div>
      
      <div className="form-group">
        <label>Icon (Small)</label>
        <input
          type="text"
          value={logo.icon || ''}
          onChange={(e) => updateBrand({ logo: { ...logo, icon: e.target.value } })}
          placeholder="https://..."
        />
      </div>
      
      <div className="form-group">
        <label>Favicon URL</label>
        <input
          type="text"
          value={logo.favicon || ''}
          onChange={(e) => updateBrand({ logo: { ...logo, favicon: e.target.value } })}
          placeholder="https://..."
        />
      </div>
    </div>
  );
};

// ============================================================================
// NAVIGATION PANEL
// ============================================================================

const NavigationPanel = ({ settings, updateNavigation }) => {
  const nav = settings.navigation || {};
  const headerLinks = nav.headerLinks || [];
  const headerCta = nav.headerCta || {};
  const socialLinks = nav.socialLinks || [];

  const updateHeaderLink = (index, updates) => {
    const newLinks = [...headerLinks];
    newLinks[index] = { ...newLinks[index], ...updates };
    updateNavigation({ headerLinks: newLinks });
  };

  const addHeaderLink = () => {
    updateNavigation({ 
      headerLinks: [...headerLinks, { label: 'New Link', url: '/', type: 'link' }] 
    });
  };

  const removeHeaderLink = (index) => {
    updateNavigation({ 
      headerLinks: headerLinks.filter((_, i) => i !== index) 
    });
  };

  const updateSocialLink = (index, updates) => {
    const newLinks = [...socialLinks];
    newLinks[index] = { ...newLinks[index], ...updates };
    updateNavigation({ socialLinks: newLinks });
  };

  return (
    <div className="navigation-panel">
      <h3>Header Navigation</h3>
      
      <div className="nav-links-list">
        {headerLinks.map((link, idx) => (
          <div key={idx} className="nav-link-item">
            <input
              type="text"
              value={link.label}
              onChange={(e) => updateHeaderLink(idx, { label: e.target.value })}
              placeholder="Label"
            />
            <input
              type="text"
              value={link.url}
              onChange={(e) => updateHeaderLink(idx, { url: e.target.value })}
              placeholder="URL"
            />
            <select
              value={link.type}
              onChange={(e) => updateHeaderLink(idx, { type: e.target.value })}
            >
              <option value="link">Page Link</option>
              <option value="anchor">Anchor (#)</option>
              <option value="external">External</option>
            </select>
            <button onClick={() => removeHeaderLink(idx)} className="btn-remove">
              <X size={14} />
            </button>
          </div>
        ))}
        <button onClick={addHeaderLink} className="btn-add-item">
          <Plus size={14} /> Add Link
        </button>
      </div>
      
      <h4>Header CTA Button</h4>
      <div className="form-row">
        <div className="form-group">
          <label>Button Text</label>
          <input
            type="text"
            value={headerCta.text || ''}
            onChange={(e) => updateNavigation({ headerCta: { ...headerCta, text: e.target.value } })}
          />
        </div>
        <div className="form-group">
          <label>Button URL</label>
          <input
            type="text"
            value={headerCta.url || ''}
            onChange={(e) => updateNavigation({ headerCta: { ...headerCta, url: e.target.value } })}
          />
        </div>
      </div>
      
      <h4>Social Links</h4>
      <div className="social-links-list">
        {socialLinks.map((link, idx) => (
          <div key={idx} className="social-link-item">
            <select
              value={link.platform}
              onChange={(e) => updateSocialLink(idx, { platform: e.target.value })}
            >
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">Twitter/X</option>
              <option value="linkedin">LinkedIn</option>
              <option value="youtube">YouTube</option>
              <option value="tiktok">TikTok</option>
            </select>
            <input
              type="text"
              value={link.url}
              onChange={(e) => updateSocialLink(idx, { url: e.target.value })}
              placeholder="Profile URL"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// SEO PANEL
// ============================================================================

const SEOPanel = ({ globalSEO, pageSEO, updateGlobalSEO, updatePageSEO, currentPage }) => {
  return (
    <div className="seo-panel">
      <h3>Page SEO ({currentPage})</h3>
      
      <div className="form-group">
        <label>Page Title</label>
        <input
          type="text"
          value={pageSEO?.title || ''}
          onChange={(e) => updatePageSEO({ title: e.target.value })}
          placeholder={`Use default: ${globalSEO?.defaultTitle || ''}`}
        />
      </div>
      
      <div className="form-group">
        <label>Page Description</label>
        <textarea
          value={pageSEO?.description || ''}
          onChange={(e) => updatePageSEO({ description: e.target.value })}
          rows={3}
          placeholder={`Use default: ${globalSEO?.defaultDescription || ''}`}
        />
      </div>
      
      <div className="form-group">
        <label>OG Image URL</label>
        <input
          type="text"
          value={pageSEO?.ogImage || ''}
          onChange={(e) => updatePageSEO({ ogImage: e.target.value })}
          placeholder="https://..."
        />
      </div>
      
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={pageSEO?.noIndex || false}
            onChange={(e) => updatePageSEO({ noIndex: e.target.checked })}
          />
          No Index (Hide from search engines)
        </label>
      </div>
      
      <hr className="divider" />
      
      <h3>Global SEO Defaults</h3>
      
      <div className="form-group">
        <label>Default Title</label>
        <input
          type="text"
          value={globalSEO?.defaultTitle || ''}
          onChange={(e) => updateGlobalSEO({ defaultTitle: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Title Suffix</label>
        <input
          type="text"
          value={globalSEO?.titleSuffix || ''}
          onChange={(e) => updateGlobalSEO({ titleSuffix: e.target.value })}
          placeholder=" | All Secured"
        />
      </div>
      
      <div className="form-group">
        <label>Google Analytics ID</label>
        <input
          type="text"
          value={globalSEO?.googleAnalyticsId || ''}
          onChange={(e) => updateGlobalSEO({ googleAnalyticsId: e.target.value })}
          placeholder="G-XXXXXXXXXX"
        />
      </div>
      
      <div className="form-group">
        <label>Meta Pixel ID</label>
        <input
          type="text"
          value={globalSEO?.metaPixelId || ''}
          onChange={(e) => updateGlobalSEO({ metaPixelId: e.target.value })}
          placeholder="XXXXXXXXXXXXXXX"
        />
      </div>
    </div>
  );
};

// ============================================================================
// SECTION EDITOR
// ============================================================================

const SectionEditor = ({ section, updateContent, updateStyle, siteSettings }) => {
  if (!section) return null;

  const renderContentEditor = () => {
    switch (section.type) {
      case 'hero':
        return <HeroEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'intro':
        return <IntroEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'text':
        return <TextEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'bulletList':
        return <BulletListEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'testimonials':
        return <TestimonialsEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'cta':
        return <CTAEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'contact':
        return <ContactEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'faq':
        return <FAQEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      case 'courseShowcase':
        return <CourseShowcaseEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
      default:
        return <GenericEditor section={section} updateContent={updateContent} updateStyle={updateStyle} />;
    }
  };

  return (
    <div className="section-editor">
      <div className="editor-section-header">
        <h2>{getSectionTypeLabel(section.type)}</h2>
        <span className="section-id-badge">ID: {section.id}</span>
      </div>
      
      {renderContentEditor()}
      
      <StyleEditor section={section} updateStyle={updateStyle} />
    </div>
  );
};

// ============================================================================
// CONTENT EDITORS
// ============================================================================

const HeroEditor = ({ section, updateContent, updateStyle }) => {
  const content = section.content || {};
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Headline</label>
        <textarea
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
          rows={3}
        />
      </div>
      
      <div className="form-group">
        <label>Subheadline</label>
        <textarea
          value={content.subheadline || ''}
          onChange={(e) => updateContent(section.id, { subheadline: e.target.value })}
          rows={2}
        />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label>Primary Button Text</label>
          <input
            type="text"
            value={content.ctaText || ''}
            onChange={(e) => updateContent(section.id, { ctaText: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Primary Button Link</label>
          <input
            type="text"
            value={content.ctaLink || ''}
            onChange={(e) => updateContent(section.id, { ctaLink: e.target.value })}
          />
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label>Secondary Button Text</label>
          <input
            type="text"
            value={content.secondaryCtaText || ''}
            onChange={(e) => updateContent(section.id, { secondaryCtaText: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Secondary Button Link</label>
          <input
            type="text"
            value={content.secondaryCtaLink || ''}
            onChange={(e) => updateContent(section.id, { secondaryCtaLink: e.target.value })}
          />
        </div>
      </div>
      
      <div className="form-group">
        <label>Tagline (below buttons)</label>
        <input
          type="text"
          value={content.tagline || ''}
          onChange={(e) => updateContent(section.id, { tagline: e.target.value })}
        />
      </div>
      
      <h3><Video size={18} /> Media</h3>
      
      <div className="form-group">
        <label>Background Video URL</label>
        <input
          type="text"
          value={content.backgroundVideo || ''}
          onChange={(e) => updateContent(section.id, { backgroundVideo: e.target.value })}
          placeholder="https://..."
        />
      </div>
      
      <div className="form-group">
        <label>Background Image URL (fallback)</label>
        <input
          type="text"
          value={content.backgroundImage || ''}
          onChange={(e) => updateContent(section.id, { backgroundImage: e.target.value })}
          placeholder="https://..."
        />
      </div>
    </div>
  );
};

const IntroEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  
  const updateParagraph = (index, value) => {
    const paragraphs = [...(content.paragraphs || [])];
    paragraphs[index] = value;
    updateContent(section.id, { paragraphs });
  };
  
  const updateBulletPoint = (index, value) => {
    const bulletPoints = [...(content.bulletPoints || [])];
    bulletPoints[index] = value;
    updateContent(section.id, { bulletPoints });
  };
  
  const addBulletPoint = () => {
    const bulletPoints = [...(content.bulletPoints || []), 'New bullet point'];
    updateContent(section.id, { bulletPoints });
  };
  
  const removeBulletPoint = (index) => {
    const bulletPoints = (content.bulletPoints || []).filter((_, i) => i !== index);
    updateContent(section.id, { bulletPoints });
  };
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Section Label</label>
        <input
          type="text"
          value={content.label || ''}
          onChange={(e) => updateContent(section.id, { label: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Headline</label>
        <textarea
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
          rows={2}
        />
      </div>
      
      <div className="form-group">
        <label>Paragraphs</label>
        {(content.paragraphs || []).map((para, idx) => (
          <textarea
            key={idx}
            value={para}
            onChange={(e) => updateParagraph(idx, e.target.value)}
            rows={3}
            className="mb-2"
          />
        ))}
      </div>
      
      <div className="form-group">
        <label>Bullet Points</label>
        {(content.bulletPoints || []).map((point, idx) => (
          <div key={idx} className="bullet-point-input">
            <input
              type="text"
              value={point}
              onChange={(e) => updateBulletPoint(idx, e.target.value)}
            />
            <button onClick={() => removeBulletPoint(idx)} className="btn-remove">
              <X size={14} />
            </button>
          </div>
        ))}
        <button onClick={addBulletPoint} className="btn-add-item">
          <Plus size={14} /> Add Bullet Point
        </button>
      </div>
    </div>
  );
};

const TextEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Headline</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Body Text</label>
        <textarea
          value={content.body || ''}
          onChange={(e) => updateContent(section.id, { body: e.target.value })}
          rows={6}
        />
      </div>
    </div>
  );
};

const BulletListEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  const bullets = content.bullets || [];
  
  const updateBullet = (index, value) => {
    const newBullets = [...bullets];
    newBullets[index] = value;
    updateContent(section.id, { bullets: newBullets });
  };
  
  const addBullet = () => {
    updateContent(section.id, { bullets: [...bullets, 'New item'] });
  };
  
  const removeBullet = (index) => {
    updateContent(section.id, { bullets: bullets.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Headline</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Icon Style</label>
        <select
          value={content.icon || 'check'}
          onChange={(e) => updateContent(section.id, { icon: e.target.value })}
        >
          <option value="check">Checkmark</option>
          <option value="arrow">Arrow</option>
          <option value="star">Star</option>
          <option value="shield">Shield</option>
        </select>
      </div>
      
      <div className="form-group">
        <label>Bullet Points</label>
        {bullets.map((bullet, idx) => (
          <div key={idx} className="bullet-point-input">
            <input
              type="text"
              value={bullet}
              onChange={(e) => updateBullet(idx, e.target.value)}
            />
            <button onClick={() => removeBullet(idx)} className="btn-remove">
              <X size={14} />
            </button>
          </div>
        ))}
        <button onClick={addBullet} className="btn-add-item">
          <Plus size={14} /> Add Item
        </button>
      </div>
    </div>
  );
};

const TestimonialsEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  const testimonials = content.testimonials || [];
  
  const updateTestimonial = (index, field, value) => {
    const updated = [...testimonials];
    updated[index] = { ...updated[index], [field]: value };
    updateContent(section.id, { testimonials: updated });
  };
  
  const addTestimonial = () => {
    updateContent(section.id, { 
      testimonials: [...testimonials, { id: `t${Date.now()}`, quote: 'New testimonial', author: 'Author Name', role: '' }] 
    });
  };
  
  const removeTestimonial = (index) => {
    updateContent(section.id, { 
      testimonials: testimonials.filter((_, i) => i !== index) 
    });
  };
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Section Headline</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Layout</label>
        <select
          value={content.layout || 'grid'}
          onChange={(e) => updateContent(section.id, { layout: e.target.value })}
        >
          <option value="grid">Grid</option>
          <option value="carousel">Carousel</option>
          <option value="masonry">Masonry</option>
        </select>
      </div>
      
      <div className="form-group">
        <label>Testimonials</label>
        {testimonials.map((testimonial, idx) => (
          <div key={testimonial.id || idx} className="testimonial-editor">
            <div className="testimonial-header">
              <span>Testimonial {idx + 1}</span>
              <button onClick={() => removeTestimonial(idx)} className="btn-remove">
                <Trash2 size={14} />
              </button>
            </div>
            <textarea
              value={testimonial.quote}
              onChange={(e) => updateTestimonial(idx, 'quote', e.target.value)}
              placeholder="Quote"
              rows={3}
            />
            <input
              type="text"
              value={testimonial.author}
              onChange={(e) => updateTestimonial(idx, 'author', e.target.value)}
              placeholder="Author name"
            />
            <input
              type="text"
              value={testimonial.role || ''}
              onChange={(e) => updateTestimonial(idx, 'role', e.target.value)}
              placeholder="Role / Title (optional)"
            />
          </div>
        ))}
        <button onClick={addTestimonial} className="btn-add-item">
          <Plus size={14} /> Add Testimonial
        </button>
      </div>
    </div>
  );
};

const CTAEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Headline</label>
        <textarea
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
          rows={2}
        />
      </div>
      
      <div className="form-group">
        <label>Subheadline</label>
        <input
          type="text"
          value={content.subheadline || ''}
          onChange={(e) => updateContent(section.id, { subheadline: e.target.value })}
        />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label>Button Text</label>
          <input
            type="text"
            value={content.ctaText || ''}
            onChange={(e) => updateContent(section.id, { ctaText: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Button Link</label>
          <input
            type="text"
            value={content.ctaLink || ''}
            onChange={(e) => updateContent(section.id, { ctaLink: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

const ContactEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Headline</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Subheadline</label>
        <input
          type="text"
          value={content.subheadline || ''}
          onChange={(e) => updateContent(section.id, { subheadline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Submit Button Text</label>
        <input
          type="text"
          value={content.submitText || 'Send Message'}
          onChange={(e) => updateContent(section.id, { submitText: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Success Message</label>
        <input
          type="text"
          value={content.successMessage || ''}
          onChange={(e) => updateContent(section.id, { successMessage: e.target.value })}
        />
      </div>
    </div>
  );
};

const FAQEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  const faqs = content.faqs || [];
  
  const updateFAQ = (index, field, value) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    updateContent(section.id, { faqs: updated });
  };
  
  const addFAQ = () => {
    updateContent(section.id, { 
      faqs: [...faqs, { id: `faq${Date.now()}`, question: 'New Question?', answer: 'Answer here...' }] 
    });
  };
  
  const removeFAQ = (index) => {
    updateContent(section.id, { faqs: faqs.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Section Headline</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>FAQs</label>
        {faqs.map((faq, idx) => (
          <div key={faq.id || idx} className="faq-editor">
            <div className="faq-header">
              <span>FAQ {idx + 1}</span>
              <button onClick={() => removeFAQ(idx)} className="btn-remove">
                <Trash2 size={14} />
              </button>
            </div>
            <input
              type="text"
              value={faq.question}
              onChange={(e) => updateFAQ(idx, 'question', e.target.value)}
              placeholder="Question"
            />
            <textarea
              value={faq.answer}
              onChange={(e) => updateFAQ(idx, 'answer', e.target.value)}
              placeholder="Answer"
              rows={3}
            />
          </div>
        ))}
        <button onClick={addFAQ} className="btn-add-item">
          <Plus size={14} /> Add FAQ
        </button>
      </div>
    </div>
  );
};

const CourseShowcaseEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      
      <div className="form-group">
        <label>Headline</label>
        <input
          type="text"
          value={content.headline || ''}
          onChange={(e) => updateContent(section.id, { headline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Subheadline</label>
        <input
          type="text"
          value={content.subheadline || ''}
          onChange={(e) => updateContent(section.id, { subheadline: e.target.value })}
        />
      </div>
      
      <div className="form-group">
        <label>Max Courses to Show</label>
        <input
          type="number"
          value={content.maxCourses || 3}
          onChange={(e) => updateContent(section.id, { maxCourses: parseInt(e.target.value) })}
          min={1}
          max={10}
        />
      </div>
      
      <div className="form-group">
        <label>Layout</label>
        <select
          value={content.layout || 'grid'}
          onChange={(e) => updateContent(section.id, { layout: e.target.value })}
        >
          <option value="grid">Grid</option>
          <option value="carousel">Carousel</option>
          <option value="list">List</option>
        </select>
      </div>
      
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={content.showAllLink !== false}
            onChange={(e) => updateContent(section.id, { showAllLink: e.target.checked })}
          />
          Show "View All Courses" link
        </label>
      </div>
    </div>
  );
};

const GenericEditor = ({ section, updateContent }) => {
  const content = section.content || {};
  
  return (
    <div className="content-editor">
      <h3><Type size={18} /> Content</h3>
      <p className="editor-note">Edit raw content for this section type.</p>
      
      <div className="form-group">
        <label>Content (JSON)</label>
        <textarea
          value={JSON.stringify(content, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              updateContent(section.id, parsed);
            } catch (err) {}
          }}
          rows={10}
          className="code-editor"
        />
      </div>
    </div>
  );
};

// ============================================================================
// STYLE EDITOR
// ============================================================================

const StyleEditor = ({ section, updateStyle }) => {
  const style = section.style || {};
  
  const animationOptions = [
    { value: 'none', label: 'No Animation' },
    { value: 'fadeIn', label: 'Fade In' },
    { value: 'fadeInUp', label: 'Fade In Up' },
    { value: 'fadeInDown', label: 'Fade In Down' },
    { value: 'slideUp', label: 'Slide Up' },
    { value: 'slideIn', label: 'Slide In' },
    { value: 'pulse', label: 'Pulse' },
    { value: 'bounce', label: 'Bounce' },
  ];

  const layoutOptions = [
    { value: 'centered', label: 'Centered' },
    { value: 'split-left', label: 'Split (Text Left)' },
    { value: 'split-right', label: 'Split (Text Right)' },
    { value: 'full-width', label: 'Full Width' },
  ];
  
  return (
    <div className="style-editor">
      <h3><Palette size={18} /> Style & Layout</h3>
      
      <div className="form-row">
        <div className="form-group">
          <label>Background Type</label>
          <select
            value={style.backgroundType || 'solid'}
            onChange={(e) => updateStyle(section.id, { backgroundType: e.target.value })}
          >
            <option value="solid">Solid Color</option>
            <option value="gradient">Gradient</option>
            <option value="image">Image</option>
          </select>
        </div>
        <div className="form-group">
          <label>Background Color</label>
          <input
            type="text"
            value={style.backgroundColor || ''}
            onChange={(e) => updateStyle(section.id, { backgroundColor: e.target.value })}
            placeholder="#0B2239"
          />
        </div>
      </div>

      {style.backgroundType === 'gradient' && (
        <div className="form-group">
          <label>Gradient Colors (comma separated)</label>
          <input
            type="text"
            value={(style.gradientColors || []).join(', ')}
            onChange={(e) => updateStyle(section.id, { gradientColors: e.target.value.split(',').map(c => c.trim()) })}
            placeholder="#16B1C4, #f59e0b"
          />
        </div>
      )}
      
      <div className="form-row">
        <div className="form-group">
          <label>Text Color</label>
          <input
            type="text"
            value={style.textColor || ''}
            onChange={(e) => updateStyle(section.id, { textColor: e.target.value })}
            placeholder="#FFFFFF"
          />
        </div>
        <div className="form-group">
          <label>Text Alignment</label>
          <select
            value={style.textAlign || 'left'}
            onChange={(e) => updateStyle(section.id, { textAlign: e.target.value })}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label>Layout</label>
          <select
            value={style.layout || 'centered'}
            onChange={(e) => updateStyle(section.id, { layout: e.target.value })}
          >
            {layoutOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Animation</label>
          <select
            value={style.animation || 'none'}
            onChange={(e) => updateStyle(section.id, { animation: e.target.value })}
          >
            {animationOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {section.type === 'hero' && (
        <div className="form-group">
          <label>Overlay Opacity: {Math.round((style.overlayOpacity || 0.6) * 100)}%</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={style.overlayOpacity || 0.6}
            onChange={(e) => updateStyle(section.id, { overlayOpacity: parseFloat(e.target.value) })}
          />
        </div>
      )}

      <div className="form-group">
        <label>Min Height</label>
        <input
          type="text"
          value={style.minHeight || ''}
          onChange={(e) => updateStyle(section.id, { minHeight: e.target.value })}
          placeholder="auto, 50vh, 400px"
        />
      </div>
    </div>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getSectionTypeLabel = (type) => {
  const labels = {
    hero: 'Hero Section',
    intro: 'Introduction',
    text: 'Text Block',
    bulletList: 'Bullet List',
    features: 'Features Grid',
    testimonials: 'Testimonials',
    cta: 'Call to Action',
    contact: 'Contact Form',
    faq: 'FAQ Section',
    courseShowcase: 'Course Showcase',
    courseCurriculum: 'Course Curriculum',
    courseHero: 'Course Hero',
    courseCTA: 'Course CTA',
    video: 'Video Section',
    imageGallery: 'Image Gallery',
    logoStrip: 'Logo Strip',
  };
  return labels[type] || type;
};

const createNewSection = (type) => {
  const id = `${type}-${Date.now()}`;
  const defaults = {
    hero: {
      content: {
        headline: 'Your Headline Here',
        subheadline: 'Your subheadline goes here',
        ctaText: 'Get Started',
        ctaLink: '/courses',
      },
      style: { animation: 'fadeInUp', textAlign: 'center', minHeight: '80vh' }
    },
    intro: {
      content: {
        label: 'Section Label',
        headline: 'Section Headline',
        paragraphs: ['Your paragraph text here.'],
        bulletPoints: ['First point', 'Second point']
      },
      style: { animation: 'fadeIn', backgroundColor: '#FFFFFF', textColor: '#0B2239' }
    },
    text: {
      content: { headline: 'Section Title', body: 'Your content here...' },
      style: { animation: 'fadeIn' }
    },
    bulletList: {
      content: { headline: "What You'll Learn", bullets: ['Item 1', 'Item 2', 'Item 3'], icon: 'check' },
      style: { animation: 'fadeIn', backgroundColor: '#FFFFFF', textColor: '#0B2239' }
    },
    testimonials: {
      content: {
        headline: 'What People Say',
        testimonials: [{ id: 't1', quote: 'Great service!', author: 'John D.', role: '' }],
        layout: 'grid'
      },
      style: { animation: 'fadeIn' }
    },
    cta: {
      content: {
        headline: 'Ready to Get Started?',
        subheadline: 'Take action today.',
        ctaText: 'Start Now',
        ctaLink: '/signup'
      },
      style: { animation: 'pulse', backgroundType: 'gradient', gradientColors: ['#16B1C4', '#f59e0b'] }
    },
    contact: {
      content: { headline: 'Contact Us', subheadline: "We'd love to hear from you.", submitText: 'Send Message' },
      style: { animation: 'fadeIn' }
    },
    faq: {
      content: {
        headline: 'Frequently Asked Questions',
        faqs: [{ id: 'faq1', question: 'Sample question?', answer: 'Sample answer.' }]
      },
      style: { animation: 'fadeIn' }
    },
    courseShowcase: {
      content: { headline: 'Our Courses', maxCourses: 3, showAllLink: true, layout: 'grid' },
      style: { animation: 'slideUp' }
    },
    video: {
      content: { headline: 'Watch Video', videoUrl: '', autoplay: false },
      style: { animation: 'fadeIn' }
    },
    imageGallery: {
      content: { headline: 'Gallery', images: [] },
      style: { animation: 'fadeIn' }
    },
    logoStrip: {
      content: { headline: 'Trusted By', logos: [] },
      style: { animation: 'fadeIn' }
    },
  };
  
  return {
    id,
    type,
    enabled: true,
    content: defaults[type]?.content || {},
    style: defaults[type]?.style || {}
  };
};

// ============================================================================
// LIVE PREVIEW COMPONENTS
// ============================================================================

const LivePreview = ({ sections, settings, activeSection, previewMode }) => {
  const colors = settings?.colors || {};
  
  const previewStyle = {
    '--primary': colors.primary || '#16B1C4',
    '--secondary': colors.secondary || '#0B2239',
    '--accent': colors.accent || '#f59e0b',
    '--background': colors.background || '#0B2239',
    '--background-light': colors.backgroundLight || '#162e47',
    '--text': colors.text || '#FFFFFF',
    '--text-muted': colors.textMuted || 'rgba(255, 255, 255, 0.7)',
  };

  return (
    <div className="live-preview" style={previewStyle}>
      {sections.filter(s => s.enabled).map(section => (
        <div 
          key={section.id} 
          className={`preview-section ${activeSection === section.id ? 'highlighted' : ''}`}
          data-section-id={section.id}
        >
          <PreviewSection section={section} settings={settings} />
        </div>
      ))}
    </div>
  );
};

const PreviewSection = ({ section, settings }) => {
  switch (section.type) {
    case 'hero':
      return <HeroPreview section={section} settings={settings} />;
    case 'intro':
      return <IntroPreview section={section} />;
    case 'bulletList':
      return <BulletListPreview section={section} />;
    case 'testimonials':
      return <TestimonialsPreview section={section} />;
    case 'cta':
      return <CTAPreview section={section} />;
    case 'contact':
      return <ContactPreview section={section} />;
    case 'faq':
      return <FAQPreview section={section} />;
    case 'courseShowcase':
      return <CourseShowcasePreview section={section} />;
    case 'text':
      return <TextPreview section={section} />;
    default:
      return <GenericPreview section={section} />;
  }
};

const HeroPreview = ({ section, settings }) => {
  const content = section.content || {};
  const style = section.style || {};
  
  return (
    <div 
      className="preview-hero"
      style={{
        backgroundImage: content.backgroundImage ? `url(${content.backgroundImage})` : 'none',
        textAlign: style.textAlign || 'center',
        minHeight: style.minHeight || '80vh'
      }}
    >
      {content.backgroundVideo && (
        <div className="preview-hero-video">
          <video autoPlay muted loop playsInline>
            <source src={content.backgroundVideo} type="video/mp4" />
          </video>
        </div>
      )}
      <div className="preview-hero-overlay" style={{ opacity: style.overlayOpacity || 0.6 }} />
      <div className="preview-hero-content">
        <h1>{content.headline || 'Hero Headline'}</h1>
        <p>{content.subheadline || 'Hero subheadline text'}</p>
        <div className="preview-hero-buttons">
          {content.ctaText && (
            <button className="preview-btn primary">{content.ctaText}</button>
          )}
          {content.secondaryCtaText && (
            <button className="preview-btn secondary">{content.secondaryCtaText}</button>
          )}
        </div>
        {content.tagline && <span className="preview-tagline">{content.tagline}</span>}
      </div>
    </div>
  );
};

const IntroPreview = ({ section }) => {
  const content = section.content || {};
  const style = section.style || {};
  
  return (
    <div 
      className="preview-intro"
      style={{ 
        backgroundColor: style.backgroundColor || '#FFFFFF',
        color: style.textColor || '#0B2239'
      }}
    >
      {content.label && <span className="preview-intro-label">{content.label}</span>}
      <h2>{content.headline || 'Section Headline'}</h2>
      {(content.paragraphs || []).map((para, idx) => (
        <p key={idx}>{para}</p>
      ))}
      {content.bulletPoints && content.bulletPoints.length > 0 && (
        <ul className="preview-bullet-list">
          {content.bulletPoints.map((point, idx) => (
            <li key={idx}>
              <CheckCircle2 size={14} />
              {point}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BulletListPreview = ({ section }) => {
  const content = section.content || {};
  const style = section.style || {};
  
  return (
    <div 
      className="preview-bullet-section"
      style={{ 
        backgroundColor: style.backgroundColor || '#FFFFFF',
        color: style.textColor || '#0B2239'
      }}
    >
      <h2>{content.headline || 'Bullet List'}</h2>
      <ul className="preview-bullet-list">
        {(content.bullets || []).map((bullet, idx) => (
          <li key={idx}>
            <CheckCircle2 size={14} />
            {bullet}
          </li>
        ))}
      </ul>
    </div>
  );
};

const TestimonialsPreview = ({ section }) => {
  const content = section.content || {};
  const testimonials = content.testimonials || [];
  
  return (
    <div className="preview-testimonials">
      <h2>{content.headline || 'Testimonials'}</h2>
      <div className="preview-testimonials-grid">
        {testimonials.slice(0, 3).map((t, idx) => (
          <div key={t.id || idx} className="preview-testimonial-card">
            <Quote size={20} className="quote-icon" />
            <p>"{t.quote}"</p>
            <span className="author">— {t.author}</span>
            {t.role && <span className="role">{t.role}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const CTAPreview = ({ section }) => {
  const content = section.content || {};
  const style = section.style || {};
  
  let bgStyle = {};
  if (style.backgroundType === 'gradient' && style.gradientColors?.length >= 2) {
    bgStyle = { background: `linear-gradient(135deg, ${style.gradientColors.join(', ')})` };
  } else {
    bgStyle = { backgroundColor: style.backgroundColor || 'var(--primary)' };
  }
  
  return (
    <div className="preview-cta" style={bgStyle}>
      <h2>{content.headline || 'Call to Action'}</h2>
      {content.subheadline && <p>{content.subheadline}</p>}
      {content.ctaText && (
        <button className="preview-btn cta">
          {content.ctaText}
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
};

const ContactPreview = ({ section }) => {
  const content = section.content || {};
  
  return (
    <div className="preview-contact">
      <h2>{content.headline || 'Contact Us'}</h2>
      {content.subheadline && <p className="subtitle">{content.subheadline}</p>}
      <div className="preview-contact-form">
        <div className="form-field">
          <label>Name</label>
          <div className="input-placeholder" />
        </div>
        <div className="form-field">
          <label>Email</label>
          <div className="input-placeholder" />
        </div>
        <div className="form-field">
          <label>Message</label>
          <div className="textarea-placeholder" />
        </div>
        <button className="preview-btn primary">
          <Phone size={14} />
          {content.submitText || 'Send Message'}
        </button>
      </div>
    </div>
  );
};

const FAQPreview = ({ section }) => {
  const content = section.content || {};
  const faqs = content.faqs || [];
  
  return (
    <div className="preview-faq">
      <h2>{content.headline || 'FAQ'}</h2>
      <div className="preview-faq-list">
        {faqs.map((faq, idx) => (
          <div key={faq.id || idx} className="preview-faq-item">
            <div className="faq-question">
              <ChevronDown size={16} />
              {faq.question}
            </div>
            <div className="faq-answer">{faq.answer}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CourseShowcasePreview = ({ section }) => {
  const content = section.content || {};
  
  return (
    <div className="preview-courses">
      <h2>{content.headline || 'Featured Courses'}</h2>
      <div className="preview-courses-grid">
        {[1, 2, 3].slice(0, content.maxCourses || 3).map(i => (
          <div key={i} className="preview-course-card">
            <div className="course-card-image" />
            <div className="course-card-content">
              <h3>Course Title {i}</h3>
              <p>Course description preview...</p>
              <div className="course-card-footer">
                <span className="price">$49</span>
                <button className="preview-btn small">View</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {content.showAllLink && (
        <a className="preview-link-btn">View All Courses <ArrowRight size={14} /></a>
      )}
    </div>
  );
};

const TextPreview = ({ section }) => {
  const content = section.content || {};
  const style = section.style || {};
  
  return (
    <div 
      className="preview-text"
      style={{ 
        backgroundColor: style.backgroundColor || 'transparent',
        color: style.textColor || 'var(--text)',
        textAlign: style.textAlign || 'left'
      }}
    >
      <h2>{content.headline || 'Section Title'}</h2>
      <p>{content.body || 'Section content goes here...'}</p>
    </div>
  );
};

const GenericPreview = ({ section }) => {
  return (
    <div className="preview-generic">
      <div className="generic-placeholder">
        <Layout size={24} />
        <span>{getSectionTypeLabel(section.type)}</span>
      </div>
    </div>
  );
};

export default VisualEditor;
